// Struktogramm-Editor: SVG-Arbeitsfläche, Palette, Block-Inhalte, Schritt-für-Schritt-Ablauf, Prüfen.
// Bedienung ausschließlich über Pointer Events, damit Finger, Stift und Maus gleich funktionieren.
// Es gibt keine Pfeile: Blöcke werden in Sequenzen eingefügt (unter dem ausgewählten Block oder in
// eine leere Stelle) und lassen sich samt Inhalt verschieben.

import { confetti } from "./confetti.ts";
import { ICON } from "./icons.ts";
import { BLOCK_LABEL, ExprError, Runner, check, childrenOf, compileBlock, compileProgram, outputsMatch, shownOutputs, starsFor,
         toPseudocode } from "./sim.ts";
import type { CheckResult, Field, Program, SBlock, Struktogramm, Task, Wert } from "./sim.ts";
import { store } from "./store.ts";

const W = 800;
const PAD = 16;
const ROW = 54;        // Höhe einer Zeile (einfacher Block, Schleifenkopf/-fuß)
const HEAD = 80;       // Kopf einer Verzweigung (Dreieck)
const INDENT = 40;     // Breite des Schleifen-Balkens links
const SLOT = 46;       // leere Stelle
const BOTTOM = 80;     // Ablagefläche „ans Ende“ unter dem Struktogramm
const EDGE_SHIFT = 12; // Fangzonen an Kanten: knapp darüber = innen, knapp darunter = außen
const AUTOSCROLL = 70; // Randabstand (px), ab dem beim Ziehen mitgescrollt wird
const TAP_SLOP = 6;    // Pixel, ab denen eine Berührung als Ziehen gilt
const PLAY_MS = 650;

const PALETTE = ["eingabe", "anweisung", "ausgabe", "verzweigung", "solange", "wiederhole", "zaehlschleife"];
const PALETTE_LABEL: Record<string, string> = {
  anweisung: "Anweisung", eingabe: "Eingabe", ausgabe: "Ausgabe", verzweigung: "Verzweigung",
  solange: "Solange", wiederhole: "Wiederhole bis", zaehlschleife: "Zählschleife",
};
const PLACEHOLDER: Record<string, string> = {
  anweisung: "x = …", eingabe: "a, b", ausgabe: "x", verzweigung: "a > b",
  solange: "b ≠ 0", wiederhole: "n = 0", zaehlschleife: "i = 1 bis n",
};
const EXAMPLE: Record<string, string> = {
  anweisung: "Zuweisung, z. B. summe = summe + x",
  eingabe: "Variablen, die eingelesen werden, z. B. a, b",
  ausgabe: 'Werte oder Texte, z. B. "Ergebnis:", x',
  verzweigung: "Bedingung, z. B. a > b -- links steht der Ja-, rechts der Nein-Zweig",
  solange: "Bedingung, die vor jedem Durchlauf geprüft wird, z. B. b ≠ 0",
  wiederhole: "Abbruchbedingung, nach jedem Durchlauf geprüft: Schluss, sobald sie gilt, z. B. n = 0",
  zaehlschleife: "Zählvariable, Start und Ende, z. B. i = 1 bis n (rückwärts: i = n bis 1 schritt -1)",
};
/** Schlüsselwort, das im Block vor dem eingegebenen Text steht (und wie man es abstreift). */
const KEYWORD: Record<string, [string, RegExp]> = {
  eingabe: ["Eingabe:", /^\s*(?:eingabe|lies)\b\s*:?\s*/i],
  ausgabe: ["Ausgabe:", /^\s*(?:ausgabe|ausgeben|gib\s+aus)\b\s*:?\s*/i],
  verzweigung: ["", /^\s*(?:wenn|falls)\s+/i],
  solange: ["solange", /^\s*solange\s+/i],
  wiederhole: ["bis", /^\s*bis\s+/i],
  zaehlschleife: ["für", /^\s*(?:für|fuer|for)\s+/i],
};
const SYMBOL_KEYS = ["=", "≠", "<", "≤", ">", "≥", "+", "−", "*", "/", "mod", "und", "oder", "bis", "(", ")", ",", '"'];

type Point = { x: number; y: number };
type SeqRef = { parent: string | null; field: Field | "root" };
type Selection = { kind: "block"; id: string } | { kind: "slot"; ref: SeqRef } | null;
/**
 * Einfügestelle: vor dem Block mit Index `index` in der Sequenz `ref`. `y` ist die gezeichnete
 * Linie, `hitY` die Höhe, an der die Stelle beim Ziehen „fängt“ -- an Kanten, wo mehrere Stellen
 * übereinanderliegen (Ende des Schleifenrumpfs = nach der Schleife), leicht nach oben bzw. unten
 * versetzt. `label` sagt in Worten, wo der Block landet.
 */
type Gap = {
  ref: SeqRef; index: number; x: number; w: number; y: number; hitY: number;
  slot: boolean; path: string[]; label: string;
};
type Area = { x: number; y: number; w: number; h: number };
type Drag =
  | { kind: "move"; id: string; client: Point; moved: boolean; gap: Gap | null; trash: boolean; at: Point | null }
  | { kind: "new"; type: string; moved: boolean; gap: Gap | null; at: Point | null };

const esc = (s: string) => s.replace(/[&<>"]/g, c => `&#${c.charCodeAt(0)};`);
const refKey = (r: SeqRef) => `${r.parent ?? ""}|${r.field}`;
const sameRef = (a: SeqRef, b: SeqRef) => a.parent === b.parent && a.field === b.field;
const isControl = (t: string) => childrenOf(t).length > 0;

/** Schriftgröße und ggf. gekürzter Text, damit der Inhalt in die Breite passt. */
function fit(text: string, maxW: number): { s: string; size: number } {
  const per = 0.6; // mittlere Zeichenbreite (Monospace) in em
  const sizeFor = (len: number) => Math.min(19, maxW / (Math.max(len, 1) * per));
  if (sizeFor(text.length) >= 12) return { s: text, size: sizeFor(text.length) };
  const keep = Math.max(1, Math.floor(maxW / (12 * per)) - 1);
  return { s: text.slice(0, keep) + "…", size: 12 };
}

/** Kleines Symbol für die Palette (Form des Blocks im Struktogramm). */
function paletteIcon(type: string): string {
  const r = (x: number, y: number, w: number, h: number) => `<rect x="${x}" y="${y}" width="${w}" height="${h}"/>`;
  let body: string;
  switch (type) {
    case "verzweigung":
      body = r(1, 1, 82, 38) + `<path d="M1 1L42 22L83 1M1 22H83M42 22V39"/>`;
      break;
    case "solange": case "zaehlschleife":
      body = `<path d="M1 1H83V14H16V39H1Z"/>` + r(16, 14, 67, 25);
      break;
    case "wiederhole":
      body = `<path d="M1 1H16V26H83V39H1Z"/>` + r(16, 1, 67, 25);
      break;
    default:
      body = r(1, 8, 82, 24);
  }
  return `<svg viewBox="0 0 84 40" aria-hidden="true" class="ns-icon">${body}</svg>`;
}

export class Editor {
  private prog: Struktogramm;
  private selection: Selection = null;
  private drag: Drag | null = null;
  private undoStack: string[] = [];
  private editing = false;
  private result: CheckResult | null = null;
  private hintsShown: number;
  private caseIdx = 0;
  private runner: Runner | null = null;
  private prevEnv = new Map<string, number>();
  private playTimer: number | null = null;
  private popStars = false;
  private showCode = false;
  private gaps: Gap[] = [];
  private areas = new Map<string, Area>(); // Fläche je Sequenz (für die Markierung beim Ziehen)
  private bottom = 0;                      // Unterkante des Struktogramms (SVG-Koordinaten)

  private readonly root: HTMLElement;
  private readonly task: Task;
  private readonly next: { id: string; titel: string } | null;
  private readonly svg: SVGSVGElement;
  private readonly layer: SVGGElement;
  private readonly palette: HTMLElement;
  private readonly side: HTMLElement;
  private readonly editBar: HTMLElement;
  private readonly code: HTMLElement;

  constructor(root: HTMLElement, task: Task, next: { id: string; titel: string } | null = null) {
    this.root = root;
    this.task = task;
    this.next = next;
    this.prog = store.program(task.id) ?? { body: [] };
    this.hintsShown = store.hints(task.id);

    root.innerHTML = `
      <section class="task-head">
        <a class="back" href="#">← Aufgaben</a>
        <h1>${esc(task.titel)}${task.optional ? ` <span class="tag optional">Optional</span>` : ""}</h1>
        <p class="barometer" hidden></p>
        ${task.text ? `<p>${esc(task.text)}</p>` : ""}
      </section>
      <div class="workbench">
        <aside class="palette" aria-label="Bausteine"></aside>
        <div class="canvas-wrap">
          <svg class="canvas" viewBox="0 0 ${W} 300" role="application" aria-label="Struktogramm">
            <g class="layer"></g>
          </svg>
          <pre class="pseudo" hidden></pre>
          <div class="edit-bar" hidden></div>
          <div class="toolbar">
            <button data-act="undo" title="Rückgängig">${ICON.undo}<span>Rückgängig</span></button>
            <button data-act="delete" title="Ausgewählten Block löschen">${ICON.trash}<span>Löschen</span></button>
            <button data-act="clear" title="Alles entfernen">Neu</button>
            <button data-act="code" class="secondary" title="Dasselbe Programm als Pseudocode">Pseudocode</button>
            <span class="spacer"></span>
            <button data-act="hint" class="secondary"></button>
            <button data-act="check" class="primary">Prüfen</button>
          </div>
        </div>
        <aside class="side"></aside>
      </div>`;
    this.svg = root.querySelector("svg.canvas")!;
    this.layer = this.svg.querySelector("g.layer")!;
    this.palette = root.querySelector(".palette")!;
    this.side = root.querySelector(".side")!;
    this.editBar = root.querySelector(".edit-bar")!;
    this.code = root.querySelector(".pseudo")!;

    this.svg.addEventListener("pointerdown", e => this.onSvgDown(e));
    this.svg.addEventListener("pointermove", e => this.onMove(e));
    this.svg.addEventListener("pointerup", e => this.onUp(e));
    this.svg.addEventListener("pointercancel", () => { this.drag = null; this.render(); });
    // Scrollen per Touch nur verhindern, wenn pointerdown (kommt vor touchstart) ein Ziehen begonnen hat
    this.svg.addEventListener("touchstart", e => { if (this.drag) e.preventDefault(); }, { passive: false });
    root.querySelector(".toolbar")!.addEventListener("click", e => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act;
      if (act) this.action(act);
    });
    this.side.addEventListener("click", e => {
      const el = e.target as HTMLElement;
      const row = el.closest<HTMLElement>("[data-case]");
      const act = el.closest<HTMLElement>("[data-run]")?.dataset.run;
      if (act) this.runAction(act);
      else if (row) this.selectCase(Number(row.dataset.case));
    });
    document.addEventListener("keydown", this.onKey);
    this.renderPalette();
    this.renderEditBar();
    this.render();
  }

  destroy(): void {
    this.stopPlay();
    document.removeEventListener("keydown", this.onKey);
  }

  // ---------------------------------------------------------------- Baum

  /** Block samt der Sequenz, in der er steht. */
  private find(id: string): { block: SBlock; seq: SBlock[]; index: number; ref: SeqRef } | null {
    const walk = (seq: SBlock[], ref: SeqRef): ReturnType<Editor["find"]> => {
      for (let i = 0; i < seq.length; i++) {
        const b = seq[i];
        if (b.id === id) return { block: b, seq, index: i, ref };
        for (const f of childrenOf(b.type)) {
          const hit = walk(b[f] ?? [], { parent: b.id, field: f });
          if (hit) return hit;
        }
      }
      return null;
    };
    return walk(this.prog.body, { parent: null, field: "root" });
  }

  private seqAt(ref: SeqRef): SBlock[] | null {
    if (ref.parent === null) return this.prog.body;
    const b = this.find(ref.parent)?.block;
    if (!b || ref.field === "root") return null;
    return (b[ref.field] ??= []);
  }

  private allIds(): Set<string> {
    const ids = new Set<string>();
    const walk = (seq: SBlock[] | undefined) => seq?.forEach(b => {
      ids.add(b.id);
      childrenOf(b.type).forEach(f => walk(b[f]));
    });
    walk(this.prog.body);
    return ids;
  }

  private newId(): string {
    const ids = this.allIds();
    let k = 1;
    while (ids.has(`b${k}`)) k++;
    return `b${k}`;
  }

  // ---------------------------------------------------------------- Änderungen

  private snapshot(): void {
    this.undoStack.push(JSON.stringify(this.prog));
    if (this.undoStack.length > 100) this.undoStack.shift();
  }

  private changed(): void {
    this.stopPlay();
    this.result = null;
    this.runner = null;
    store.saveProgram(this.task.id, this.prog);
  }

  private mutate(fn: () => void): void {
    this.snapshot();
    fn();
    this.changed();
    this.render();
  }

  private makeBlock(type: string): SBlock {
    const b: SBlock = { id: this.newId(), type, text: "" };
    for (const f of childrenOf(type)) b[f] = [];
    return b;
  }

  /** Antippen in der Palette: in die gewählte leere Stelle bzw. unter den gewählten Block. */
  private addByTap(type: string): void {
    const sel = this.selection;
    let ref: SeqRef = { parent: null, field: "root" };
    let index = this.prog.body.length;
    if (sel?.kind === "slot") {
      ref = sel.ref;
      index = 0;
    } else if (sel?.kind === "block") {
      const hit = this.find(sel.id);
      if (hit) { ref = hit.ref; index = hit.index + 1; }
    }
    this.insert(type, ref, index);
  }

  private insert(type: string, ref: SeqRef, index: number): void {
    const b = this.makeBlock(type);
    this.mutate(() => this.seqAt(ref)?.splice(index, 0, b));
    this.select({ kind: "block", id: b.id });
    this.render();
  }

  private moveTo(id: string, gap: Gap): void {
    const hit = this.find(id);
    if (!hit || gap.path.includes(id)) return;
    let index = gap.index;
    if (sameRef(hit.ref, gap.ref)) {
      if (index === hit.index || index === hit.index + 1) return; // bleibt, wo er ist
      if (index > hit.index) index--;
    }
    this.mutate(() => {
      hit.seq.splice(hit.index, 1);
      this.seqAt(gap.ref)?.splice(index, 0, hit.block);
    });
  }

  private deleteBlock(id: string): void {
    const hit = this.find(id);
    if (!hit) return;
    this.select(null);
    this.mutate(() => hit.seq.splice(hit.index, 1));
  }

  private select(sel: Selection): void {
    const key = (s: Selection) => (s ? (s.kind === "block" ? s.id : refKey(s.ref)) : "");
    const same = sel?.kind === this.selection?.kind && key(sel) === key(this.selection);
    this.selection = sel;
    if (!same) this.renderEditBar();
  }

  private action(act: string): void {
    switch (act) {
      case "undo": {
        const prev = this.undoStack.pop();
        if (!prev) return;
        this.prog = JSON.parse(prev);
        this.changed();
        this.select(null);
        this.render();
        return;
      }
      case "delete":
        if (this.selection?.kind === "block") this.deleteBlock(this.selection.id);
        return;
      case "clear":
        if (this.prog.body.length) {
          this.select(null);
          this.mutate(() => { this.prog = { body: [] }; });
        }
        return;
      case "code":
        this.showCode = !this.showCode;
        this.render();
        return;
      case "hint":
        if (this.hintsShown < this.task.hinweise.length) {
          this.hintsShown++;
          store.saveHints(this.task.id, this.hintsShown);
          this.render();
        }
        return;
      case "check": return this.runCheck();
    }
  }

  private runCheck(): void {
    this.stopPlay();
    const r = check(this.task, this.prog);
    this.result = r;
    this.root.dispatchEvent(new CustomEvent("checked", {
      detail: { result: r, program: this.prog, hints: this.hintsShown },
    }));
    if (r.solved) {
      const stars = starsFor(r, this.hintsShown);
      const before = store.stars();
      store.saveStars(this.task.id, stars);
      this.root.dispatchEvent(new CustomEvent("solved", { detail: { stars, before, result: r } }));
      confetti();
      this.popStars = true;
    } else if (r.falsche_testfaelle.length) {
      this.caseIdx = r.falsche_testfaelle[0];
    }
    this.runner = null;
    this.render();
    this.popStars = false;
  }

  // ---------------------------------------------------------------- Ablauf (Schritt für Schritt)

  private program(): Program | null {
    const prog = compileProgram(this.prog);
    return prog.problems.length ? null : prog;
  }

  private ensureRunner(): Runner | null {
    if (this.runner) return this.runner;
    const prog = this.program();
    if (!prog) return null;
    const tf = this.task.testfaelle[this.caseIdx];
    this.runner = new Runner(prog, tf.eingabe, this.task.max_schritte);
    this.prevEnv = new Map();
    return this.runner;
  }

  private selectCase(i: number): void {
    if (!this.task.testfaelle[i]) return;
    this.stopPlay();
    this.caseIdx = i;
    this.runner = null;
    this.render();
  }

  private stepOnce(): void {
    const r = this.ensureRunner();
    if (!r) return;
    this.prevEnv = new Map(r.env);
    r.step();
  }

  private runAction(act: string): void {
    switch (act) {
      case "step":
        this.stopPlay();
        if (this.runner) this.stepOnce();
        else this.ensureRunner(); // erster Druck: Startpunkt zeigen (erster Block leuchtet)
        break;
      case "play": {
        if (this.playTimer !== null) { this.stopPlay(); break; }
        const r = this.ensureRunner();
        if (!r) return;
        if (r.status !== "läuft") { this.runner = null; this.ensureRunner(); }
        this.playTimer = window.setInterval(() => {
          this.stepOnce();
          if (this.runner?.status !== "läuft") this.stopPlay();
          this.render();
        }, PLAY_MS);
        break;
      }
      case "end":
        this.stopPlay();
        this.ensureRunner()?.runToEnd();
        this.prevEnv = new Map(this.runner?.env ?? []);
        break;
      case "restart":
        this.stopPlay();
        this.runner = null;
        break;
    }
    this.render();
  }

  private stopPlay(): void {
    if (this.playTimer !== null) window.clearInterval(this.playTimer);
    this.playTimer = null;
  }

  // ---------------------------------------------------------------- Pointer

  private onKey = (e: KeyboardEvent): void => {
    if ((e.target as HTMLElement).closest("input, textarea")) return;
    if ((e.key === "Delete" || e.key === "Backspace") && this.selection?.kind === "block") {
      e.preventDefault();
      this.deleteBlock(this.selection.id);
    }
    if (e.key === "z" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.action("undo"); }
    if (e.key === "Escape") { this.select(null); this.render(); }
  };

  private toSvg(e: { clientX: number; clientY: number }): Point {
    const m = this.svg.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  }

  private overPalette(e: { clientX: number; clientY: number }): boolean {
    const r = this.palette.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  }

  private overCanvas(e: { clientX: number; clientY: number }): boolean {
    const r = this.svg.getBoundingClientRect();
    return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
  }

  /**
   * Einfügestelle unter dem Zeiger. Unterhalb des Struktogramms immer „ans Ende“; sonst die Stelle
   * mit dem kleinsten Abstand zu ihrer Fanghöhe (bei Gleichstand die tiefer geschachtelte).
   */
  private nearestGap(p: Point, exclude?: string): Gap | null {
    const usable = this.gaps.filter(g => !(exclude && g.path.includes(exclude)));
    if (p.y > this.bottom) {
      return usable.find(g => g.ref.parent === null && g.index === this.prog.body.length) ?? null;
    }
    let best: Gap | null = null, bestD = Infinity;
    for (const g of usable) {
      if (p.x < g.x - 4 || p.x > g.x + g.w + 4) continue;
      const d = g.slot && p.y >= g.y - SLOT / 2 && p.y <= g.y + SLOT / 2 ? 0 : Math.abs(p.y - g.hitY);
      if (d < bestD - 0.5 || (Math.abs(d - bestD) <= 0.5 && best && g.w < best.w)) { best = g; bestD = d; }
    }
    return bestD <= 70 ? best : null;
  }

  /** Beim Ziehen in Randnähe die Seite mitscrollen (Touch scrollt sonst nicht). */
  private autoScroll(e: { clientY: number }): void {
    const h = window.innerHeight;
    if (e.clientY > h - AUTOSCROLL) window.scrollBy(0, Math.min(24, (e.clientY - (h - AUTOSCROLL)) / 2 + 4));
    else if (e.clientY < AUTOSCROLL) window.scrollBy(0, -Math.min(24, (AUTOSCROLL - e.clientY) / 2 + 4));
  }

  private onSvgDown(e: PointerEvent): void {
    if (this.drag || !e.isPrimary) return;
    const el = (e.target as Element).closest<SVGElement>("[data-block],[data-slot]");
    if (el?.dataset.block) {
      this.svg.setPointerCapture(e.pointerId);
      this.drag = { kind: "move", id: el.dataset.block, client: { x: e.clientX, y: e.clientY },
                    moved: false, gap: null, trash: false, at: null };
    } else if (el?.dataset.slot) {
      const [parent, field] = el.dataset.slot.split("|");
      this.select({ kind: "slot", ref: { parent: parent || null, field: field as Field | "root" } });
      this.render();
    } else {
      this.select(null);
      this.render();
    }
  }

  private onMove(e: PointerEvent): void {
    const d = this.drag;
    if (!d || d.kind !== "move") return;
    if (!d.moved && Math.hypot(e.clientX - d.client.x, e.clientY - d.client.y) < TAP_SLOP) return;
    d.moved = true;
    this.autoScroll(e);
    d.trash = this.overPalette(e);
    d.at = d.trash ? null : this.toSvg(e);
    d.gap = d.at ? this.nearestGap(d.at, d.id) : null;
    this.palette.classList.toggle("trash-hot", d.trash);
    this.render();
  }

  private onUp(e: PointerEvent): void {
    const d = this.drag;
    if (!d || d.kind !== "move") return;
    this.drag = null;
    this.palette.classList.remove("trash-hot");
    if (!d.moved) {
      const same = this.selection?.kind === "block" && this.selection.id === d.id;
      this.select(same ? null : { kind: "block", id: d.id });
    } else if (this.overPalette(e)) {
      this.deleteBlock(d.id);
      return;
    } else if (d.gap) {
      this.moveTo(d.id, d.gap);
      this.select({ kind: "block", id: d.id });
    }
    this.render();
  }

  private onPaletteDown(e: PointerEvent, type: string, btn: HTMLElement): void {
    if (!e.isPrimary) return;
    e.preventDefault();
    btn.setPointerCapture(e.pointerId);
    const start = { x: e.clientX, y: e.clientY };
    const drag: Drag = { kind: "new", type, moved: false, gap: null, at: null };
    this.drag = drag;
    const move = (ev: PointerEvent) => {
      if (!drag.moved && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < TAP_SLOP) return;
      drag.moved = true;
      this.autoScroll(ev);
      drag.at = this.overCanvas(ev) ? this.toSvg(ev) : null;
      drag.gap = drag.at ? this.nearestGap(drag.at) : null;
      this.render();
    };
    const up = () => {
      btn.removeEventListener("pointermove", move);
      btn.removeEventListener("pointerup", up);
      btn.removeEventListener("pointercancel", up);
      this.drag = null;
      if (!drag.moved) this.addByTap(type);
      else if (drag.gap) this.insert(type, drag.gap.ref, drag.gap.index);
      this.render();
      this.focusEdit();
    };
    btn.addEventListener("pointermove", move);
    btn.addEventListener("pointerup", up);
    btn.addEventListener("pointercancel", up);
  }

  // ---------------------------------------------------------------- Block-Inhalt bearbeiten

  private selectedBlock(): SBlock | undefined {
    return this.selection?.kind === "block" ? this.find(this.selection.id)?.block : undefined;
  }

  private focusEdit(): void {
    const input = this.editBar.querySelector<HTMLInputElement>("input");
    if (input && !input.value) input.focus();
  }

  private renderEditBar(): void {
    const b = this.selectedBlock();
    if (this.selection?.kind === "slot") {
      this.editBar.hidden = false;
      this.editBar.innerHTML = `<p class="edit-msg">Leere Stelle ausgewählt: Tippen Sie links auf einen Baustein,
        er wird hier eingefügt.</p>`;
      return;
    }
    this.editBar.hidden = !b;
    if (!b) { this.editBar.innerHTML = ""; return; }
    const kw = KEYWORD[b.type]?.[0];
    this.editBar.innerHTML = `
      <label class="edit-row"><span class="edit-type">${BLOCK_LABEL[b.type]}</span>
        ${kw ? `<span class="edit-kw">${esc(kw)}</span>` : ""}
        <input type="text" value="${esc(b.text ?? "")}" placeholder="${esc(PLACEHOLDER[b.type])}"
          autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false"
          enterkeyhint="done" aria-label="Inhalt des Blocks"></label>
      <div class="keys">${SYMBOL_KEYS.map(k => `<button type="button" data-key="${esc(k)}">${esc(k)}</button>`).join("")}</div>
      <p class="edit-msg"></p>`;
    const input = this.editBar.querySelector("input")!;
    input.addEventListener("input", () => {
      const block = this.selectedBlock();
      if (!block) return;
      if (!this.editing) { this.snapshot(); this.editing = true; }
      block.text = input.value;
      this.changed();
      this.render();
    });
    input.addEventListener("blur", () => { this.editing = false; });
    input.addEventListener("keydown", e => { if (e.key === "Enter") input.blur(); });
    this.editBar.querySelector(".keys")!.addEventListener("pointerdown", e => {
      const key = (e.target as HTMLElement).closest<HTMLElement>("[data-key]")?.dataset.key;
      if (!key) return;
      e.preventDefault(); // Fokus (und Tastatur) im Eingabefeld lassen
      const pad = /^[a-z]/.test(key) ? ` ${key} ` : key;
      const a = input.selectionStart ?? input.value.length, z = input.selectionEnd ?? a;
      input.setRangeText(pad, a, z, "end");
      input.dispatchEvent(new Event("input"));
      input.focus();
    });
  }

  private renderEditMsg(): void {
    const b = this.selectedBlock();
    const msg = this.editBar.querySelector<HTMLElement>(".edit-msg");
    if (!b || !msg) return;
    let text = EXAMPLE[b.type], cls = "edit-msg";
    if ((b.text ?? "").trim()) {
      try {
        compileBlock(b.type, b.text ?? "");
        text = "Inhalt ist gültig.";
        cls += " ok";
      } catch (err) {
        text = err instanceof ExprError ? err.message : String(err);
        cls += " bad";
      }
    }
    msg.className = cls;
    msg.textContent = text;
  }

  // ---------------------------------------------------------------- Darstellung

  private renderPalette(): void {
    this.palette.innerHTML = `<div class="palette-title">Bausteine</div>` +
      PALETTE.map(type => `<button class="part" data-type="${type}"
          title="${BLOCK_LABEL[type]} -- antippen (kommt unter den ausgewählten Block) oder an die gewünschte Stelle ziehen">
          ${paletteIcon(type)}<span class="part-name">${PALETTE_LABEL[type]}</span></button>`).join("")
      + `<div class="trash-hint">Hierher ziehen zum Löschen</div>`;
    this.palette.querySelectorAll<HTMLElement>("button.part").forEach(btn =>
      btn.addEventListener("pointerdown", e => this.onPaletteDown(e, btn.dataset.type!, btn)));
  }

  /** Text eines Blocks: [Schlüsselwort, Inhalt ohne doppeltes Schlüsselwort]. */
  private label(b: SBlock): [string, string] {
    const [kw, re] = KEYWORD[b.type] ?? ["", /^$/];
    return [kw, (b.text ?? "").trim().replace(re, "")];
  }

  private textSvg(b: SBlock, x: number, y: number, maxW: number, anchor: "start" | "middle"): string {
    const [kw, text] = this.label(b);
    if (!text) {
      return `<text x="${x}" y="${y}" class="blk-text placeholder" text-anchor="${anchor}">${kw ? `${esc(kw)} ` : ""}${esc(PLACEHOLDER[b.type])}</text>`;
    }
    const f = fit(kw ? `${kw} ${text}` : text, maxW);
    const shown = kw ? f.s.slice(kw.length + 1) : f.s;
    return `<text x="${x}" y="${y}" class="blk-text" text-anchor="${anchor}" style="font-size:${f.size.toFixed(1)}px">` +
      `${kw ? `<tspan class="kw">${esc(kw)} </tspan>` : ""}${esc(shown)}</text>`;
  }

  private render(): void {
    const r = this.result;
    const run = this.runner;
    const bad = new Set<string>([...(r?.problems ?? []).map(p => p.ref ?? ""), ...(run?.ref ? [run.ref] : [])]);
    const sel = this.selection;
    const dragId = this.drag?.kind === "move" && this.drag.moved ? this.drag.id : null;
    const parts: string[] = [];
    const outlines: string[] = [];
    this.gaps = [];
    this.areas.clear();

    const blockClass = (b: SBlock) => {
      let cls = `blk ${b.type}`;
      if (sel?.kind === "block" && sel.id === b.id) cls += " selected";
      if (run?.cur === b.id && run.status === "läuft") cls += " current";
      if (run && run.status !== "läuft" && run.status !== "beendet" && run.ref === b.id) cls += " failed";
      if (bad.has(b.id) && !run) cls += " error";
      if ((b.text ?? "").trim()) { try { compileBlock(b.type, b.text ?? ""); } catch { cls += " invalid"; } }
      if (dragId === b.id) cls += " dragging";
      return cls;
    };

    const lay = (seq: SBlock[], ref: SeqRef, path: string[], x: number, y: number, w: number, minH = SLOT): number => {
      if (!seq.length) {
        const key = refKey(ref);
        const selected = sel?.kind === "slot" && refKey(sel.ref) === key ? " selected" : "";
        const h = minH;
        const help = ref.parent === null
          ? `<text x="${x + w / 2}" y="${y + h / 2 - 16}" class="slot-text">Tippen Sie links auf einen Baustein</text>
             <text x="${x + w / 2}" y="${y + h / 2 + 16}" class="slot-text">oder ziehen Sie ihn hierher.</text>`
          : `<text x="${x + w / 2}" y="${y + h / 2}" class="slot-text">${ref.field === "nein" || ref.field === "ja" ? "∅" : "leer"}</text>`;
        parts.push(`<g class="slot${selected}" data-slot="${esc(key)}">
          <rect x="${x}" y="${y}" width="${w}" height="${h}" class="slot-bg"/>${help}</g>`);
        this.gaps.push({ ref, index: 0, x, w, y: y + h / 2, hitY: y + h / 2, slot: true, path,
                         label: this.gapLabel(ref, seq, 0) });
        this.areas.set(key, { x, y, w, h });
        return h;
      }
      const nested = ref.parent !== null;
      const gap = (index: number, gy: number) => {
        // Kanten entzerren: Ende einer inneren Sequenz fängt knapp darüber, die Stelle nach
        // einer Schleife/Verzweigung knapp darunter -- sonst lägen beide auf derselben Höhe.
        let hitY = gy;
        if (nested && index === seq.length) hitY -= EDGE_SHIFT;
        if (index > 0 && isControl(seq[index - 1].type)) hitY += EDGE_SHIFT;
        this.gaps.push({ ref, index, x, w, y: gy, hitY, slot: false, path, label: this.gapLabel(ref, seq, index) });
      };
      let cy = y;
      gap(0, cy);
      seq.forEach((b, i) => {
        cy += block(b, ref, path, x, cy, w);
        gap(i + 1, cy);
      });
      this.areas.set(refKey(ref), { x, y, w, h: cy - y });
      return cy - y;
    };

    const block = (b: SBlock, _ref: SeqRef, path: string[], x: number, y: number, w: number): number => {
      const mark = parts.length;
      parts.push(""); // Platz für den eigenen Bereich -- Kinder liegen darüber
      const inner = [...path, b.id];
      let h: number, own: string, extra = "";
      switch (b.type) {
        case "verzweigung": {
          const half = w / 2;
          const hJa = lay(b.ja ?? [], { parent: b.id, field: "ja" }, inner, x, y + HEAD, half);
          const hNein = lay(b.nein ?? [], { parent: b.id, field: "nein" }, inner, x + half, y + HEAD, w - half);
          const body = Math.max(hJa, hNein);
          h = HEAD + body;
          own = `<rect x="${x}" y="${y}" width="${w}" height="${HEAD}" class="own"/>`;
          // kürzere Spalte auffüllen (gehört optisch zur Verzweigung)
          if (hJa < body) own += `<rect x="${x}" y="${y + HEAD + hJa}" width="${half}" height="${body - hJa}" class="own filler"/>`;
          if (hNein < body) own += `<rect x="${x + half}" y="${y + HEAD + hNein}" width="${w - half}" height="${body - hNein}" class="own filler"/>`;
          const taken = run?.taken.get(b.id);
          extra = `<path d="M${x} ${y}L${x + half} ${y + HEAD}L${x + w} ${y}" class="tri"/>` +
            this.textSvg(b, x + half, y + 24, w * 0.62, "middle") +
            `<text x="${x + 10}" y="${y + HEAD - 10}" class="branch${taken === "ja" ? " taken" : ""}">ja</text>` +
            `<text x="${x + w - 10}" y="${y + HEAD - 10}" class="branch end${taken === "nein" ? " taken" : ""}">nein</text>`;
          break;
        }
        case "solange": case "zaehlschleife": {
          const hb = lay(b.body ?? [], { parent: b.id, field: "body" }, inner, x + INDENT, y + ROW, w - INDENT);
          h = ROW + hb;
          own = `<path d="M${x} ${y}H${x + w}V${y + ROW}H${x + INDENT}V${y + h}H${x}Z" class="own"/>`;
          extra = this.textSvg(b, x + 12, y + ROW / 2, w - 70, "start") + this.rounds(b, x + w - 10, y + ROW / 2);
          break;
        }
        case "wiederhole": {
          const hb = lay(b.body ?? [], { parent: b.id, field: "body" }, inner, x + INDENT, y, w - INDENT);
          h = hb + ROW;
          own = `<path d="M${x} ${y}H${x + INDENT}V${y + hb}H${x + w}V${y + h}H${x}Z" class="own"/>`;
          extra = this.textSvg(b, x + 12, y + hb + ROW / 2, w - 70, "start") + this.rounds(b, x + w - 10, y + hb + ROW / 2);
          break;
        }
        default: {
          h = ROW;
          own = `<rect x="${x}" y="${y}" width="${w}" height="${h}" class="own"/>`;
          extra = this.textSvg(b, x + 12, y + ROW / 2, w - 24, "start");
        }
      }
      parts[mark] = `<g class="${blockClass(b)}" data-block="${esc(b.id)}">${own}${extra}</g>`;
      if (sel?.kind === "block" && sel.id === b.id && isControl(b.type)) {
        outlines.push(`<rect x="${x + 2}" y="${y + 2}" width="${w - 4}" height="${h - 4}" class="sel-outline"/>`);
      }
      return h;
    };

    const total = lay(this.prog.body, { parent: null, field: "root" }, [], PAD, PAD, W - 2 * PAD, 150);
    this.bottom = PAD + total;
    this.svg.setAttribute("viewBox", `0 0 ${W} ${this.bottom + BOTTOM}`);

    this.layer.innerHTML = parts.join("") + `<rect x="${PAD}" y="${PAD}" width="${W - 2 * PAD}" height="${total}" class="frame"/>` +
      outlines.join("") + this.dropOverlay();

    this.code.hidden = !this.showCode;
    if (this.showCode) this.code.textContent = this.prog.body.length ? toPseudocode(this.prog) : "(noch leer)";
    const codeBtn = this.root.querySelector<HTMLButtonElement>('[data-act="code"]')!;
    codeBtn.classList.toggle("active", this.showCode);
    codeBtn.setAttribute("aria-pressed", String(this.showCode));
    this.root.querySelector<HTMLButtonElement>('[data-act="delete"]')!.disabled = this.selection?.kind !== "block";
    this.root.querySelector<HTMLButtonElement>('[data-act="undo"]')!.disabled = !this.undoStack.length;
    const hintBtn = this.root.querySelector<HTMLButtonElement>('[data-act="hint"]')!;
    const left = this.task.hinweise.length - this.hintsShown;
    hintBtn.innerHTML = `${ICON.bulb}<span>Hinweis${left > 0 ? ` (${left})` : ""}</span>`;
    hintBtn.disabled = left <= 0;
    hintBtn.hidden = this.task.hinweise.length === 0;
    this.renderEditMsg();
    this.renderSide();
  }

  /** Kurzname eines Blocks für Beschriftungen, z. B. „solange b ≠ 0“. */
  private short(b: SBlock, max = 24): string {
    const [kw, text] = this.label(b);
    const s = text ? (kw ? `${kw} ${text}` : text) : BLOCK_LABEL[b.type];
    return s.length > max ? s.slice(0, max - 1) + "…" : s;
  }

  /** In Worten: wo landet ein Block, der an dieser Stelle losgelassen wird? */
  private gapLabel(ref: SeqRef, seq: SBlock[], index: number): string {
    const pos = !seq.length ? "" : index === 0 ? "am Anfang" : index === seq.length ? "am Ende" : `nach „${this.short(seq[index - 1])}“`;
    if (ref.parent === null) {
      if (!seq.length) return "als ersten Block";
      return index === 0 ? "ganz an den Anfang" : index === seq.length ? "ganz ans Ende" : pos;
    }
    const owner = this.find(ref.parent)?.block;
    if (!owner) return pos;
    const where = ref.field === "ja" ? `in den Ja-Zweig von „${this.short(owner, 18)}“`
      : ref.field === "nein" ? `in den Nein-Zweig von „${this.short(owner, 18)}“`
      : `in die Schleife „${this.short(owner, 20)}“`;
    return pos ? `${where}, ${pos}` : where;
  }

  /** Markierung beim Ziehen: Ablagefläche unten, Zielsequenz, Einfügelinie, Beschriftung, Vorschau. */
  private dropOverlay(): string {
    const d = this.drag;
    if (!d || !d.moved || (d.kind === "move" && d.trash)) return "";
    const out: string[] = [];
    const gap = d.gap;
    const atEnd = !!gap && gap.ref.parent === null && gap.index === this.prog.body.length && !gap.slot;
    // Ablagefläche „ans Ende“ unter dem Struktogramm (nur beim Ziehen sichtbar)
    if (this.prog.body.length) {
      out.push(`<rect x="${PAD}" y="${this.bottom + 10}" width="${W - 2 * PAD}" height="${BOTTOM - 20}" rx="10"
        class="drop-end${atEnd ? " hot" : ""}"/>
        <text x="${W / 2}" y="${this.bottom + BOTTOM / 2}" class="drop-end-text${atEnd ? " hot" : ""}">hier loslassen: ans Ende anhängen</text>`);
    }
    if (gap) {
      const area = this.areas.get(refKey(gap.ref));
      if (gap.ref.parent !== null && area) {
        out.push(`<rect x="${area.x}" y="${area.y}" width="${area.w}" height="${area.h}" class="drop-area"/>`);
      }
      if (gap.slot) {
        out.push(`<rect x="${gap.x + 3}" y="${gap.y - SLOT / 2 + 3}" width="${gap.w - 6}" height="${SLOT - 6}" class="drop-slot"/>`);
      } else {
        const x1 = gap.x + 6, x2 = gap.x + gap.w - 6;
        out.push(`<line x1="${x1}" y1="${gap.y}" x2="${x2}" y2="${gap.y}" class="drop-line"/>
          <circle cx="${x1}" cy="${gap.y}" r="7" class="drop-cap"/><circle cx="${x2}" cy="${gap.y}" r="7" class="drop-cap"/>`);
      }
      // Beschriftung als Schild über der Linie (bzw. darunter, wenn oben kein Platz ist)
      const text = `↓ ${gap.label}`;
      const tw = Math.min(W - 16, text.length * 8.4 + 24);
      const lx = Math.max(8, Math.min(gap.x + gap.w - tw - 4, W - tw - 8));
      const ly = gap.slot ? gap.y - SLOT / 2 - 34 : gap.y - 40 < 2 ? gap.y + 10 : gap.y - 40;
      out.push(`<g class="drop-label"><rect x="${lx}" y="${ly}" width="${tw}" height="30" rx="15"/>
        <text x="${lx + 12}" y="${ly + 15}">${esc(text)}</text></g>`);
    }
    // Vorschau des gezogenen Blocks am Zeiger (oberhalb, damit der Finger sie nicht verdeckt)
    if (d.at) {
      const name = d.kind === "new" ? PALETTE_LABEL[d.type] : (() => {
        const b = this.find(d.id)?.block;
        return b ? this.short(b, 20) : "";
      })();
      const gw = Math.max(110, name.length * 9 + 28);
      out.push(`<g class="drop-ghost${gap ? "" : " nowhere"}"><rect x="${d.at.x - gw / 2}" y="${d.at.y - 64}" width="${gw}" height="34" rx="6"/>
        <text x="${d.at.x}" y="${d.at.y - 47}">${esc(name)}</text></g>`);
    }
    return out.join("");
  }

  /** Durchlaufzähler am Schleifenkopf bzw. -fuß während des Ablaufs. */
  private rounds(b: SBlock, x: number, y: number): string {
    const n = this.runner?.rounds.get(b.id);
    return n === undefined ? "" : `<text x="${x}" y="${y}" class="rounds">${n}×</text>`;
  }

  private renderSide(): void {
    const t = this.task;
    const r = this.result;
    const run = this.runner;
    const list = (xs: Wert[]) => xs.join(", ");

    const rows = t.testfaelle.map((tf, i) => {
      const res = r?.testfaelle?.[i];
      const live = !res && run && i === this.caseIdx && run.status !== "läuft" ? run : null;
      const ist = res ? res.ausgabe : live ? shownOutputs(live.werte, tf.ausgabe) : null;
      let mark = "", cls = "";
      if (res) {
        cls = res.status === "richtig" ? "ok" : "bad";
        mark = res.status === "richtig" ? ICON.check
          : res.status === "falsch" ? ICON.cross : `<span class="why">${res.status === "endlosschleife" ? "∞" : "!"}</span>`;
      }
      return `<tr data-case="${i}" class="${i === this.caseIdx ? "current" : ""} ${cls}">
        <td class="num">${i + 1}</td>
        <td>${esc(list(tf.eingabe))}</td>
        <td class="soll">${esc(list(tf.ausgabe))}</td>
        <td class="ist">${ist === null ? "" : ist.length ? esc(list(ist)) : "–"}</td>
        <td class="mark">${mark}</td>
      </tr>`;
    }).join("");

    let feedback = "";
    if (r?.solved) {
      const stars = starsFor(r, this.hintsShown);
      const onward = this.next
        ? `<a class="next-task" href="#${this.next.id}">Nächste Aufgabe: ${esc(this.next.titel)} ${ICON.arrow}</a>`
        : `<a class="next-task" href="#">Zur Übersicht ${ICON.arrow}</a>`;
      feedback = `<div class="feedback success">
        <div class="stars${this.popStars ? " pop" : ""}">${"★".repeat(stars)}${"☆".repeat(3 - stars)}</div>
        <strong>Richtig!</strong> Alle ${t.testfaelle.length} Testfälle stimmen
        -- mit ${r.bloecke} ${r.bloecke === 1 ? "Block" : "Blöcken"}.
        ${t.par !== null && !r.par_erreicht ? `<br>Es geht auch mit ${t.par} Blöcken -- schaffen Sie das?` : ""}
        ${r.par_erreicht && this.hintsShown > 0 ? "<br>Den dritten Stern gibt es, wenn Sie es ohne Hinweis schaffen." : ""}
        ${onward}
      </div>`;
    } else if (r) {
      const msgs = r.problems.length
        ? r.problems.map(p => `<li>${esc(this.describe(p.ref))}${esc(p.message)}</li>`).join("")
        : r.testfaelle!.flatMap((c, i) => {
          if (c.status === "richtig") return [];
          const what = c.status === "falsch"
            ? `erwartet ${list(t.testfaelle[i].ausgabe) || "keine Ausgabe"}, ausgegeben ${list(c.ausgabe) || "nichts"}`
            : `${c.status === "endlosschleife" ? "Endlosschleife" : "Laufzeitfehler"}: ${c.fehler}`;
          return [`<li>Testfall ${i + 1}: ${esc(what)}</li>`];
        }).slice(0, 4).join("");
      const tip = r.problems.length ? "" : `<p>Verfolgen Sie den markierten Testfall mit „Schritt“.</p>`;
      feedback = `<div class="feedback error"><strong>Noch nicht ganz.</strong><ul>${msgs}</ul>${tip}</div>`;
    }

    const hints = t.hinweise.slice(0, this.hintsShown).map(h => `<li>${esc(h)}</li>`).join("");

    this.side.innerHTML = `
      ${feedback}
      <section class="cases">
        <h2>Testfälle</h2>
        <table class="case-table">
          <thead><tr><th></th><th>${esc(t.eingaben.join(", "))}</th><th class="soll">Soll</th>
            <th class="ist">Ist</th><th></th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </section>
      ${this.renderStepper()}
      ${hints ? `<div class="hints"><strong>Hinweise</strong><ul>${hints}</ul></div>` : ""}`;
  }

  /** „Ref“ eines Problems als lesbarer Vorspann, z. B. „Solange-Schleife b ≠ 0: “. */
  private describe(ref: string | null): string {
    if (!ref) return "";
    const b = this.find(ref)?.block;
    if (!b) return "";
    const text = (b.text ?? "").trim();
    return `${BLOCK_LABEL[b.type]}${text ? ` „${text}“` : ""}: `;
  }

  private renderStepper(): string {
    const t = this.task;
    const tf = t.testfaelle[this.caseIdx];
    const prog = this.runner ? null : compileProgram(this.prog);
    const head = `<h2>Ablauf · Testfall ${this.caseIdx + 1}
      <small>${t.eingaben.map((name, k) => `${esc(name)} = ${tf.eingabe[k]}`).join(", ")}</small></h2>`;
    if (prog && prog.problems.length) {
      const items = prog.problems.slice(0, 4).map(p => `<li>${esc(this.describe(p.ref))}${esc(p.message)}</li>`).join("");
      return `<section class="stepper">${head}<p class="muted">Zum Ausprobieren fehlt noch:</p>
        <ul class="todo">${items}</ul></section>`;
    }
    const run = this.runner;
    const playing = this.playTimer !== null;
    const done = run !== null && run.status !== "läuft";
    const buttons = `<div class="run-buttons">
      <button type="button" data-run="restart" title="Von vorn" ${run ? "" : "disabled"}>${ICON.restart}</button>
      <button type="button" data-run="step" title="Ein Schritt" ${done ? "disabled" : ""}>${ICON.step}<span>Schritt</span></button>
      <button type="button" data-run="play" title="${playing ? "Anhalten" : "Abspielen"}" ${done && !playing ? "disabled" : ""}>
        ${playing ? ICON.pause : ICON.play}</button>
      <button type="button" data-run="end" title="Bis zum Ende" ${done ? "disabled" : ""}>${ICON.end}</button>
    </div>`;

    let status = `<p class="muted">Mit „Schritt“ läuft das Struktogramm Block für Block. Der aktuelle Block leuchtet.</p>`;
    if (run) {
      const cur = run.cur ? this.find(run.cur)?.block : undefined;
      const what = cur ? `${BLOCK_LABEL[cur.type]} „${esc(cur.text ?? "")}“` : "";
      const soll = tf.ausgabe;
      const ist = shownOutputs(run.werte, soll);
      const list2 = (xs: Wert[]) => xs.length ? xs.join(", ") : "nichts";
      switch (run.status) {
        case "läuft":
          status = `<p>Schritt ${run.schritte} · als Nächstes: ${what}</p>`;
          break;
        case "beendet": {
          const ok = outputsMatch(run.werte, soll);
          status = `<p class="${ok ? "ok" : "bad"}">${ok ? ICON.check : ICON.cross} Fertig nach ${run.schritte} Schritten:
            ausgegeben ${esc(list2(ist))}${ok ? "" : `, erwartet ${esc(list2(soll))}`}.</p>`;
          break;
        }
        default: {
          const at = run.ref ? this.find(run.ref)?.block : undefined;
          const where = at ? ` in ${BLOCK_LABEL[at.type]} „${esc(at.text ?? "")}“` : "";
          status = `<p class="bad">${run.status === "endlosschleife" ? "Endlosschleife" : "Laufzeitfehler"}${where}:
            ${esc(run.fehler ?? "")}</p>`;
        }
      }
    }
    const vars = run && run.env.size
      ? `<table class="vars"><thead><tr><th>Variable</th><th>Wert</th></tr></thead><tbody>
          ${[...run.env].map(([k, v]) => `<tr class="${this.prevEnv.get(k) !== v ? "changed" : ""}">
            <td>${esc(k)}</td><td>${v}</td></tr>`).join("")}</tbody></table>`
      : "";
    const out = run && run.werte.length
      ? `<div class="console"><small>Ausgabe</small><pre>${esc(run.texte.join(" "))}</pre></div>` : "";
    return `<section class="stepper">${head}${buttons}${status}${vars}${out}</section>`;
  }
}
